import { useEffect, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'

import type { ImportResult } from '@/lib/api/customer-api'

import type { CustomerListValues } from '@/components/customers/customers-list-filters'
import type { CustomerListFilterSurface } from '@/components/customers/customers-list-filter-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { KiotVietSyncButton } from '@/components/customers/kiotviet-sync-button'
import { CustomerImportDialog } from '@/components/customers/customer-import-dialog'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { captureCustomerListFiltersChanged } from '@/components/customers/customers-list-filter-tracking'
import {
    CUSTOMER_LIST_FILTERS,
    customerListApiParams,
} from '@/components/customers/customers-list-filters'
import {
    CustomersListTable,
    debtDaysLabel,
} from '@/components/customers/customers-list-table'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { LIST_CARD_TITLE_CLASS, ListCard } from '@/components/common/ListCard'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { CustomerForm } from '@/components/forms/customer-form'
import { DebtAmount } from '@/components/customers/debt-ledger-tab'
import { ImportResultDialog } from '@/components/import-result-dialog'
import {
    useCustomerGroups,
    useCustomers,
    useImportCustomers,
} from '@/hooks/use-customer-query'
import { isWalkInCustomer } from '@/lib/constants'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/customers/')({
    component: CustomersPage,
    validateSearch: CUSTOMER_LIST_FILTERS.validateSearch,
})

export function CustomersPage() {
    const navigate = useNavigate({ from: '/customers/' })
    const urlSearch = Route.useSearch()

    // Chip lọc theo nhóm cần TÊN, URL chỉ có id → tra runtime rồi truyền xuống.
    const { data: groups = [] } = useCustomerGroups()
    const groupName = groups.find(
        (g) => g.id === (urlSearch.group_id as string | undefined),
    )?.name

    const { values, barProps, page, setPage } = useListFilters({
        def: CUSTOMER_LIST_FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
        chipLabels: {
            group_id: groupName
                ? `${m.customer_group_filter_label()}: ${groupName}`
                : undefined,
        },
    })

    const isMobile = useIsMobile()
    const applyFrom =
        (surface: CustomerListFilterSurface) => (next: CustomerListValues) => {
            captureCustomerListFiltersChanged({
                before: values,
                after: next,
                surface,
            })
            barProps.onApply(next)
        }
    const bar = {
        ...barProps,
        // Lưới desktop áp ngay, sheet mobile áp khi bấm Áp dụng — cùng một onApply.
        onApply: applyFrom(isMobile ? 'sheet' : 'bar'),
        onClear: () => {
            captureCustomerListFiltersChanged({
                before: values,
                after: CUSTOMER_LIST_FILTERS.defaults,
                surface: 'clear',
            })
            barProps.onClear()
        },
        chips: barProps.chips.map((chip) => ({
            ...chip,
            onRemove: () => {
                const key = chip.key as keyof CustomerListValues
                captureCustomerListFiltersChanged({
                    before: values,
                    after: {
                        ...values,
                        [key]: CUSTOMER_LIST_FILTERS.defaults[key],
                    },
                    surface: 'chip',
                })
                chip.onRemove()
            },
        })),
    }

    const [dialogOpen, setDialogOpen] = useState(false)
    const [importResult, setImportResult] = useState<ImportResult | null>(null)
    const [importResultOpen, setImportResultOpen] = useState(false)
    const importMutation = useImportCustomers()

    // Listen for SSE import result events
    useEffect(() => {
        const handler = (e: Event) => {
            const result = (e as CustomEvent<ImportResult>).detail
            setImportResult(result)
            setImportResultOpen(true)
        }
        window.addEventListener('customer-import-result', handler)
        return () =>
            window.removeEventListener('customer-import-result', handler)
    }, [])

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const { data, isLoading, isError } = useCustomers(
        customerListApiParams(values, page),
    )

    // Hide the built-in "Khách lẻ" walk-in customer from the management list —
    // it is a system row every POS / order-create flow depends on, not a real
    // customer users should edit or delete. While it still owes, show it: its
    // POS debt is in the totals row and accounting must see where it comes from
    // (the detail page already hides edit/delete for it).
    const customers = (data?.data ?? []).filter(
        (c) => !isWalkInCustomer(c) || (c.current_debt ?? 0) > 0,
    )
    const meta = data?.meta

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const filtering = barProps.activeCount > 0 || !!values.q
    const emptyState = isError ? (
        <p className="py-8 text-center text-destructive">
            {m.common_error_load()}
        </p>
    ) : (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {filtering ? m.list_empty_filtered() : m.customer_no_data()}
            </span>
            {filtering ? (
                <ClearFilterButton active onClear={bar.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">
                        {m.customer_title()}
                    </h1>
                </div>
                <div className="flex gap-2 items-center">
                    <KiotVietSyncButton />
                    <CustomerImportDialog
                        isPending={importMutation.isPending}
                        onImport={(params) =>
                            importMutation.mutate(params, {
                                onSuccess: () =>
                                    toast.info(m.import_processing()),
                                onError: () =>
                                    toast.error(m.import_upload_failed()),
                            })
                        }
                    />
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button size="sm">
                                <Plus className="size-4" />
                                {m.customer_add_new()}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>
                                    {m.customer_add_new()}
                                </DialogTitle>
                            </DialogHeader>
                            <CustomerForm
                                onSuccess={() => setDialogOpen(false)}
                                onCancel={() => setDialogOpen(false)}
                            />
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <ListFilterBar {...bar} />

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.common_loading()}
                    </div>
                ) : customers.length === 0 ? (
                    emptyState
                ) : (
                    customers.map((c) => (
                        <ListCard
                            key={c.id}
                            title={
                                <Link
                                    to="/customers/$customerId"
                                    params={{ customerId: c.id }}
                                    search={{ tab: 'info' }}
                                    className={LIST_CARD_TITLE_CLASS}
                                >
                                    {c.name}
                                </Link>
                            }
                            subtitle={c.code}
                            badge={
                                <Badge
                                    variant={
                                        c.is_active ? 'success' : 'secondary'
                                    }
                                >
                                    {c.is_active
                                        ? m.customer_active()
                                        : m.customer_inactive()}
                                </Badge>
                            }
                            metric={{
                                label: m.customer_debt_col_current(),
                                value: (
                                    <DebtAmount value={c.current_debt ?? 0} />
                                ),
                            }}
                            fields={[
                                {
                                    label: m.customer_phone_label(),
                                    value: c.phone ?? '—',
                                },
                                {
                                    label: m.customer_group(),
                                    value: c.customer_group?.name ?? '—',
                                },
                                {
                                    label: m.customer_debt_days_col(),
                                    value: debtDaysLabel(c),
                                },
                            ]}
                        />
                    ))
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border overflow-x-auto">
                <CustomersListTable
                    customers={customers}
                    isLoading={isLoading}
                    emptyState={emptyState}
                    totals={meta?.totals}
                    totalCount={meta?.total}
                    sort={values.sort}
                    onSortChange={(sort) =>
                        applyFrom('column_header')({ ...values, sort })
                    }
                />
            </div>

            {/* Pagination */}
            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{m.customer_total_count({ count: meta.total })}</span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {m.common_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage(page + 1)}
                        >
                            {m.common_next()}
                        </Button>
                    </div>
                </div>
            )}

            {/* Import result dialog */}
            <ImportResultDialog
                result={importResult}
                open={importResultOpen}
                onOpenChange={setImportResultOpen}
            />
        </div>
    )
}
